import { useEffect, useRef } from "react";
import { cn } from "@/lib/utils";

/**
 * A styled native checkbox. `indeterminate` is a DOM property, not an
 * attribute, so it has to be set imperatively — which is the whole reason this
 * wrapper exists (the header checkbox of a partially-selected table needs it).
 */
export function Checkbox({
  checked,
  indeterminate = false,
  onCheckedChange,
  disabled,
  className,
  ariaLabel,
}: {
  checked: boolean;
  indeterminate?: boolean;
  onCheckedChange: (v: boolean) => void;
  disabled?: boolean;
  className?: string;
  ariaLabel: string;
}) {
  const ref = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (ref.current) ref.current.indeterminate = indeterminate && !checked;
  }, [indeterminate, checked]);

  return (
    <input
      ref={ref}
      type="checkbox"
      aria-label={ariaLabel}
      checked={checked}
      disabled={disabled}
      onChange={(e) => onCheckedChange(e.target.checked)}
      className={cn(
        "size-3.5 shrink-0 cursor-pointer rounded-[3px] appearance-none align-middle",
        "border border-[var(--color-border-strong)] bg-[rgba(255,255,255,0.04)]",
        "checked:bg-[var(--color-accent-bg)] checked:border-[var(--color-accent-bg)]",
        "indeterminate:bg-[var(--color-accent-bg)] indeterminate:border-[var(--color-accent-bg)]",
        "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[var(--color-accent)] focus-visible:ring-offset-1 focus-visible:ring-offset-[var(--color-canvas)]",
        "disabled:opacity-40 disabled:cursor-not-allowed",
        "relative transition-colors",
        "checked:after:content-['✓'] checked:after:absolute checked:after:inset-0 checked:after:text-white checked:after:text-[9px] checked:after:leading-[13px] checked:after:text-center",
        "indeterminate:after:content-['–'] indeterminate:after:absolute indeterminate:after:inset-0 indeterminate:after:text-white indeterminate:after:text-[9px] indeterminate:after:leading-[12px] indeterminate:after:text-center",
        className,
      )}
    />
  );
}
